import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  PerformanceAgreementQuestionnaireSectionFragment,
  PerformanceAgreementQuestionnairesFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { CompetenciesChart } from 'modules/performance_process/components/CompetenciesChart'

import AlignedSectionableQuestions from './AlignedSectionableQuestions'
import { AgreementQuestionnaireCollapsable } from './_AgreementQuestionnaireCollapsable'

export const SectionableQuestions: React.FC<{
  agreement: PerformanceAgreementQuestionnairesFragment
}> = ({ agreement }) => {
  const reviewer = agreement.managerEvaluation?.reviewerAccess
  const target = agreement.target.access

  const selfAnswers = agreement.selfEvaluation?.answeredQuestionnaires
  const managerAnswers = agreement.managerEvaluation?.answeredQuestionnaires

  const answeredContent = (
    qestionnaire: PerformanceAgreementQuestionnaireSectionFragment | undefined | null
  ) => {
    const content = qestionnaire?.content ?? []
    const hasContent = content.length > 0

    return { content, hasContent }
  }

  const { content: selfCompetencies, hasContent: hasSelfCompetencies } = answeredContent(
    selfAnswers?.answeredCompetenciesQuestionnaire
  )
  const { content: managerCompetencies, hasContent: hasManagerCompetencies } = answeredContent(
    managerAnswers?.answeredCompetenciesQuestionnaire
  )
  const selfAnsweredCompetenciesContent = selfAnswers?.answeredCompetenciesQuestionnaire?.content
  const managerAnsweredCompetenciesContent =
    managerAnswers?.answeredCompetenciesQuestionnaire?.content

  const { content: selfQuestions, hasContent: hasSelfQuestions } = answeredContent(
    selfAnswers?.answeredQuestionnaireWithSections
  )
  const { content: managerQuestions, hasContent: hasManagerQuestions } = answeredContent(
    managerAnswers?.answeredQuestionnaireWithSections
  )

  const { content: selfEmployeeScore, hasContent: hasSelfEmployeeScore } = answeredContent(
    selfAnswers?.answeredEmployeeScoreQuestionnaire
  )
  const { content: managerEmployeeScore, hasContent: hasManagerEmployeeScore } = answeredContent(
    managerAnswers?.answeredEmployeeScoreQuestionnaire
  )

  return (
    <Box gap="s24">
      {(hasSelfCompetencies || hasManagerCompetencies) && (
        <AgreementQuestionnaireCollapsable
          title={i18n.t('performance_process.agreements.sections.competencies_title')}
        >
          <Box gap="s16">
            {!!selfAnsweredCompetenciesContent?.length &&
              !!managerAnsweredCompetenciesContent?.length && (
                <CompetenciesChart
                  selfContent={selfAnsweredCompetenciesContent ?? []}
                  managerContent={managerAnsweredCompetenciesContent ?? []}
                  scoreScale={agreement.process?.scoreScale.scale ?? []}
                />
              )}

            <AlignedSectionableQuestions
              managerContent={managerCompetencies}
              selfContent={selfCompetencies}
              agreement={agreement}
              reviewer={reviewer || undefined}
              target={target || undefined}
              hasTargetData={hasSelfCompetencies}
              hasManagerData={hasManagerCompetencies}
            />
          </Box>
        </AgreementQuestionnaireCollapsable>
      )}

      {(hasManagerQuestions || hasSelfQuestions) && (
        <AgreementQuestionnaireCollapsable
          title={i18n.t('performance_process.agreements.sections.questions_title')}
        >
          <AlignedSectionableQuestions
            managerContent={managerQuestions}
            selfContent={selfQuestions}
            agreement={agreement}
            reviewer={reviewer || undefined}
            target={target || undefined}
            hasTargetData={hasSelfQuestions}
            hasManagerData={hasManagerQuestions}
          />
        </AgreementQuestionnaireCollapsable>
      )}

      {(hasSelfEmployeeScore || hasManagerEmployeeScore) && (
        <AgreementQuestionnaireCollapsable
          title={i18n.t('performance_process.agreements.sections.overall_performance_title')}
        >
          <AlignedSectionableQuestions
            managerContent={managerEmployeeScore}
            selfContent={selfEmployeeScore}
            agreement={agreement}
            reviewer={reviewer || undefined}
            target={target || undefined}
            hasTargetData={hasSelfEmployeeScore}
            hasManagerData={hasManagerEmployeeScore}
          />
        </AgreementQuestionnaireCollapsable>
      )}
    </Box>
  )
}
